import { unzipSync, zipSync, strFromU8, strToU8 } from 'fflate'

export function safeJsonParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

export function uniqStrings(values) {
  const out = []
  const seen = new Set()
  for (const v of values || []) {
    const s = typeof v === 'string' ? v : null
    if (!s) continue
    if (seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out
}

export function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function plainTextToHtml(text) {
  const t = String(text || '').trim()
  if (!t) return ''
  return `<p>${escapeHtml(t).replace(/\n/g, '<br/>')}</p>`
}

export function buildVerificationLink(code, action = '') {
  try {
    const base = window?.location?.origin || ''
    const actionParam = action ? `&action=${encodeURIComponent(action)}` : ''
    return `${base}/validar?code=${encodeURIComponent(code)}${actionParam}`
  } catch {
    const actionParam = action ? `&action=${encodeURIComponent(action)}` : ''
    return `/validar?code=${encodeURIComponent(code)}${actionParam}`
  }
}

let xlsxLoaderPromise = null

async function ensureXLSX() {
  if (window?.XLSX) return window.XLSX
  if (xlsxLoaderPromise) return xlsxLoaderPromise

  xlsxLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.0/package/dist/xlsx.full.min.js'
    script.async = true
    script.onload = () => resolve(window.XLSX)
    script.onerror = () => reject(new Error('Falha ao carregar biblioteca de Excel'))
    document.head.appendChild(script)
  })

  return xlsxLoaderPromise
}

// Monta o XML de data validation (listas suspensas) para as colunas indicadas.
function buildDataValidationsXml(validations, firstRow, lastRow) {
  const items = validations.map((v) => (
    `<dataValidation type="list" allowBlank="1" showDropDown="0" ` +
    `sqref="${v.column}${firstRow}:${v.column}${lastRow}">` +
    `<formula1>${v.formula}</formula1></dataValidation>`
  ))
  return `<dataValidations count="${items.length}">${items.join('')}</dataValidations>`
}

// A edição Community do SheetJS descarta data validation na gravação. Para o
// template sair com as listas suspensas, o XML é injetado manualmente dentro da
// planilha (zip/xlsx) após a geração do arquivo.
async function injectDataValidations(workbookBytes, dataValidationsXml) {
  const bytes = workbookBytes instanceof Uint8Array ? workbookBytes : new Uint8Array(workbookBytes)
  const files = unzipSync(bytes)
  const sheetPath = 'xl/worksheets/sheet1.xml'
  const sheetFile = files[sheetPath]
  if (!sheetFile) return bytes

  let sheetXml = strFromU8(sheetFile)
  // Remove validações pré-existentes para evitar duplicidade.
  sheetXml = sheetXml.replace(/<dataValidations[\s\S]*?<\/dataValidations>/g, '')
  sheetXml = sheetXml.replace(/<dataValidations[^>]*\/>/g, '')

  // No schema do worksheet, dataValidations vem logo após sheetData.
  if (sheetXml.includes('</sheetData>')) {
    sheetXml = sheetXml.replace('</sheetData>', `</sheetData>${dataValidationsXml}`)
  } else if (sheetXml.includes('</worksheet>')) {
    sheetXml = sheetXml.replace('</worksheet>', `${dataValidationsXml}</worksheet>`)
  }

  files[sheetPath] = strToU8(sheetXml)
  return zipSync(files)
}

// Cor padrão da marca SOFERA (laranja), usada quando o tema não define
// --primary-color (a variável é definida pelo parceiro ativo em runtime).
export const EXPORT_BRAND_COLOR = 'F97316'

// Lê a cor da marca ativa no tema (--primary-color, definida pelo parceiro) e
// converte para o formato AARRGGBB exigido pelo styles.xml.
function resolveBrandRgb() {
  const fallback = `FF${EXPORT_BRAND_COLOR}`
  try {
    const raw = String(
      getComputedStyle(document.documentElement).getPropertyValue('--primary-color') || ''
    ).trim()
    if (!raw) return fallback

    const longHex = raw.match(/^#?([0-9a-f]{6})$/i)
    if (longHex) return `FF${longHex[1].toUpperCase()}`

    const shortHex = raw.match(/^#?([0-9a-f]{3})$/i)
    if (shortHex) {
      const [r, g, b] = shortHex[1].split('')
      return `FF${(r + r + g + g + b + b).toUpperCase()}`
    }

    const rgb = raw.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
    if (rgb) {
      const toHex = (n) => Number(n).toString(16).padStart(2, '0')
      return `FF${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`.toUpperCase()
    }
  } catch { /* ambiente sem DOM: usa o fallback da marca */ }
  return fallback
}

// Monta o styles.xml com o visual padrão do sistema: cabeçalho na cor da marca
// (--primary-color) com texto escuro em negrito, faixas claras nas linhas pares
// e borda discreta. A edição Community do SheetJS descarta estilos na gravação,
// por isso o arquivo é injetado manualmente após a geração do .xlsx.
function buildBrandedStylesXml() {
  const headerFill = resolveBrandRgb()
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="FF0F172A"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="4">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="${headerFill}"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFF1F5F9"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="2">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left style="thin"><color rgb="FFE2E8F0"/></left><right style="thin"><color rgb="FFE2E8F0"/></right><top style="thin"><color rgb="FFE2E8F0"/></top><bottom style="thin"><color rgb="FFE2E8F0"/></bottom><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="4">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="center"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`
}

// Aplica os estilos de linha/cabeçalho nas células da planilha principal.
function applyBrandedRowStyles(sheetXml, headerRow = 1) {
  return sheetXml.replace(/<c r="([A-Z]+)(\d+)"([^>]*?)(\/?)>/g, (match, col, rowNum, attrs, selfClose) => {
    const row = parseInt(rowNum, 10)
    // 1 = cabeçalho, 2 = linha clara, 3 = linha com faixa.
    const styleId = row === headerRow ? 1 : (row % 2 === 0 ? 2 : 3)
    const cleanAttrs = attrs.replace(/\s+s="[^"]*"/g, '')
    return `<c r="${col}${rowNum}" s="${styleId}"${cleanAttrs}${selfClose}>`
  })
}

// Congela a primeira linha (cabeçalho) usando o formato OOXML correto.
function ensureFrozenHeader(sheetXml) {
  if (sheetXml.includes('<pane ')) return sheetXml
  const pane = '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>'

  // <sheetView .../> auto-fechado: abre o elemento para inserir o pane.
  if (/<sheetView\b[^>]*\/>/.test(sheetXml)) {
    return sheetXml.replace(/<sheetView\b([^>]*)\/>/, (m, attrs) => `<sheetView${attrs}>${pane}</sheetView>`)
  }
  // <sheetView ...>: injeta o pane logo após a abertura.
  return sheetXml.replace(/<sheetView\b[^>]*>/, (m) => `${m}${pane}`)
}

async function injectTableStyles(workbookBytes, headerRow = 1) {
  const bytes = workbookBytes instanceof Uint8Array ? workbookBytes : new Uint8Array(workbookBytes)
  const files = unzipSync(bytes)

  files['xl/styles.xml'] = strToU8(buildBrandedStylesXml())

  const sheetPath = 'xl/worksheets/sheet1.xml'
  const sheetFile = files[sheetPath]
  if (sheetFile) {
    let sheetXml = applyBrandedRowStyles(strFromU8(sheetFile), headerRow)
    sheetXml = ensureFrozenHeader(sheetXml)
    files[sheetPath] = strToU8(sheetXml)
  }

  return zipSync(files)
}

export async function downloadUserTemplate(options = {}) {
  const cargos = Array.isArray(options?.cargos) ? options.cargos.filter(Boolean) : []
  const areas = Array.isArray(options?.areas) ? options.areas.filter(Boolean) : []

  const headers = [
    'Nome Completo',
    'E-mail',
    'CPF (Opcional)',
    'Perfil (Opcional)',
    'Celular (Opcional) ((00) 00000-0000)',
    'Cargo (Opcional)',
    'Área (Opcional)'
  ]

  const FIRST_DATA_ROW = 2
  const LAST_DATA_ROW = 1000

  try {
    const XLSX = await ensureXLSX()
    const ws = XLSX.utils.aoa_to_sheet([headers])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios')

    let dataValidationsXml = ''
    if (cargos.length > 0 || areas.length > 0) {
      // Aba auxiliar (oculta) com os valores oficiais da empresa. Ela alimenta
      // as listas suspensas (data validation) das colunas Cargo/Área.
      const listRows = [['Cargos', 'Áreas']]
      const maxLength = Math.max(cargos.length, areas.length)
      for (let i = 0; i < maxLength; i += 1) {
        listRows.push([cargos[i] || '', areas[i] || ''])
      }
      const listSheet = XLSX.utils.aoa_to_sheet(listRows)
      XLSX.utils.book_append_sheet(wb, listSheet, 'Listas')

      // Oculta a aba auxiliar. O book_new() do SheetJS não cria wb.Workbook,
      // então ele precisa ser inicializado antes de marcar a aba como oculta.
      if (!wb.Workbook) wb.Workbook = {}
      if (!Array.isArray(wb.Workbook.Sheets)) wb.Workbook.Sheets = []
      while (wb.Workbook.Sheets.length < wb.SheetNames.length) wb.Workbook.Sheets.push({})
      wb.Workbook.Sheets[1].Hidden = 1

      const validations = []
      if (cargos.length > 0) {
        validations.push({ column: 'F', formula: `Listas!$A$2:$A$${cargos.length + 1}` })
      }
      if (areas.length > 0) {
        validations.push({ column: 'G', formula: `Listas!$B$2:$B$${areas.length + 1}` })
      }
      dataValidationsXml = buildDataValidationsXml(validations, FIRST_DATA_ROW, LAST_DATA_ROW)
    }

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    // A edição Community do SheetJS descarta data validation na gravação, por
    // isso o XML da validação é injetado após a geração do arquivo.
    const fileBytes = dataValidationsXml
      ? await injectDataValidations(wbout, dataValidationsXml)
      : new Uint8Array(wbout)

    const blob = new Blob([fileBytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', 'template_importacao_usuarios.xlsx')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Erro ao gerar template Excel de usuários:', error)
    alert('Não foi possível gerar o template em Excel. Tente novamente.')
  }
}

export async function downloadAssessmentQuestionTemplate() {
  const rows = [
    [
      'Enunciado',
      'Alternativa 1',
      'Alternativa 2',
      'Alternativa 3',
      'Alternativa 4',
      'Alternativa 5 (Opcional)',
      'Resposta Correta',
      'Explicacao (Opcional)'
    ],
    [
      'Qual e a capital do Brasil?',
      'Sao Paulo',
      'Brasilia',
      'Rio de Janeiro',
      'Belo Horizonte',
      '',
      '2',
      'Brasilia e a capital federal do Brasil.'
    ]
  ]

  try {
    const XLSX = await ensureXLSX()
    const ws = XLSX.utils.aoa_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Questoes')
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })

    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', 'template_banco_questoes.xlsx')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Erro ao gerar template Excel de questoes:', error)
    alert('Nao foi possivel gerar o template em Excel. Tente novamente.')
  }
}

function normalizeExportAlternativeSlots(alternatives) {
  const slots = ['', '', '', '', '']
  const fallbackIndexById = new Map()

  ;(alternatives || []).forEach((alt, index) => {
    const rawId = String(alt?.id || '').trim().toLowerCase()
    const inferredIndex = rawId >= 'a' && rawId <= 'e'
      ? rawId.charCodeAt(0) - 97
      : index

    const safeIndex = Math.max(0, Math.min(4, inferredIndex))
    slots[safeIndex] = String(alt?.text || '')
    fallbackIndexById.set(rawId || String(index), safeIndex)
  })

  return { slots, fallbackIndexById }
}

function normalizeCorrectAnswerForExport(correctAnswer, fallbackIndexById) {
  const answers = Array.isArray(correctAnswer) ? correctAnswer : correctAnswer ? [correctAnswer] : []

  return answers
    .map((item) => {
      const rawId = String(item?.id || '').trim().toLowerCase()
      if (rawId >= 'a' && rawId <= 'e') return String(rawId.charCodeAt(0) - 96)
      if (fallbackIndexById.has(rawId)) return String(fallbackIndexById.get(rawId) + 1)
      return ''
    })
    .filter(Boolean)
    .join(', ')
}

export async function exportAssessmentQuestionsToExcel(assessmentTitle, questions) {
  const headers = [
    'Enunciado',
    'Alternativa 1',
    'Alternativa 2',
    'Alternativa 3',
    'Alternativa 4',
    'Alternativa 5 (Opcional)',
    'Resposta Correta',
    'Explicacao (Opcional)'
  ]

  const rows = (questions || []).map((question) => {
    const { slots, fallbackIndexById } = normalizeExportAlternativeSlots(question?.alternatives)
    const correctAnswer = normalizeCorrectAnswerForExport(question?.correct_answer, fallbackIndexById)

    return [
      String(question?.statement || ''),
      ...slots,
      correctAnswer,
      String(question?.explanation || '')
    ]
  })

  try {
    const XLSX = await ensureXLSX()
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Questoes')
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })

    const safeTitle = String(assessmentTitle || 'avaliacao')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'avaliacao'

    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `questoes_${safeTitle}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Erro ao exportar questoes da avaliacao:', error)
    alert('Nao foi possivel exportar as questoes em Excel. Tente novamente.')
  }
}

// Exportação genérica de uma tabela já filtrada para .xlsx.
export async function exportRowsToExcel({ fileName = 'exportacao.xlsx', sheetName = 'Dados', headers = [], rows = [] } = {}) {
  const XLSX = await ensureXLSX()
  const data = [headers, ...rows]
  const ws = XLSX.utils.aoa_to_sheet(data)

  ws['!cols'] = headers.map((header, index) => ({
    // Limite alto o bastante para URLs completas (ex.: link do curso com turma);
    // o teto de 255 é o máximo aceito pelo Excel.
    wch: Math.min(255, Math.max(12, data.reduce(
      (max, row) => Math.max(max, String(row[index] ?? '').length),
      String(header || '').length
    ) + 2))
  }))
  ws['!autofilter'] = {
    ref: `A1:${XLSX.utils.encode_col(Math.max(0, headers.length - 1))}${Math.max(1, data.length)}`
  }

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)

  // Altura confortável na linha do cabeçalho (congelamento é injetado no XML).
  ws['!rows'] = [{ hpt: 22 }]

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  // A edição Community do SheetJS descarta estilos na gravação, por isso o
  // visual da marca (cabeçalho teal, faixas e bordas) é injetado no .xlsx.
  const fileBytes = await injectTableStyles(wbout)

  const blob = new Blob([fileBytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export async function exportUsersToExcel(users, options = {}) {
  const formatLastAccess = options.formatLastAccess
  const headers = ['Nome', 'E-mail', 'Empresa', 'Cargo', 'Área', 'Perfil', 'Status', 'Último Acesso']

  const rows = (users || []).map((user) => [
    String(user?.name || ''),
    String(user?.email || ''),
    String(user?.company || ''),
    String(user?.cargo || ''),
    String(user?.area || ''),
    String(user?.profile || ''),
    String(user?.status || ''),
    typeof formatLastAccess === 'function'
      ? formatLastAccess(user)
      : (user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString('pt-BR') : 'Nunca')
  ])

  const XLSX = await ensureXLSX()
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
  ws['!cols'] = [
    { wch: 30 }, { wch: 32 }, { wch: 28 }, { wch: 20 },
    { wch: 20 }, { wch: 16 }, { wch: 14 }, { wch: 20 }
  ]
  ws['!autofilter'] = { ref: `A1:H${rows.length + 1}` }

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Usuarios')
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })

  const stamp = new Date().toISOString().split('T')[0]
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `usuarios_${stamp}.xlsx`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
